import React from "react";
import { ArrowRight, GitCommitHorizontal, Globe, Lock, Server } from "lucide-react";
import type { AssuranceAssessment, AssuranceFinding } from "../../../assurance/types";
import {
  selectArchitecture,
  selectComposition,
  selectLanguages,
  selectScan,
  selectScannerProvenance,
} from "../../../assurance/model";
import { cn } from "../../../lib/utils";
import {
  BarList,
  CaveatNote,
  EmptyPanel,
  FindingLink,
  KeyValueGrid,
  MetricTile,
  Pill,
  SectionPanel,
  SegmentStrip,
  StatusPill,
  fmtDateTime,
  fmtNumber,
  fmtPercent,
} from "../primitives";

// ---------------------------------------------------------------------------
// Coverage & provenance
// ---------------------------------------------------------------------------

export function CoverageView({
  assessment,
  demo,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
}) {
  const scan = selectScan(assessment);
  const languages = selectLanguages(assessment);
  const provenance = selectScannerProvenance(assessment);
  const repository = assessment.repository;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 xl:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
        <SectionPanel
          title="Measured coverage"
          description="What the completed scan actually analyzed and produced"
        >
          {scan.measured ? (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <MetricTile label="Findings analyzed" value={fmtNumber(scan.measured.findingsAnalyzed)} demo={demo} />
                <MetricTile label="Files with findings" value={fmtNumber(scan.measured.filesWithFindings)} demo={demo} />
                <MetricTile label="Analysis capabilities" value={fmtNumber(scan.measured.analysisCapabilities)} demo={demo} />
                <MetricTile label="Dependency components" value={fmtNumber(scan.measured.dependencyComponents)} demo={demo} />
                <MetricTile label="Languages detected" value={fmtNumber(scan.measured.languagesDetected)} demo={demo} />
                <MetricTile label="Scan status" value={scan.status} demo={demo} />
              </div>
              {scan.measured.capabilityList.length > 0 && (
                <div className="mt-4">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
                    Analysis capabilities exercised
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {scan.measured.capabilityList.map((cap) => (
                      <Pill key={cap} tone="brand">
                        {cap}
                      </Pill>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <EmptyPanel demo={demo} detail="Coverage was not reported for this scan." />
          )}
        </SectionPanel>

        <SectionPanel
          title="Commit provenance"
          description="Every conclusion is bound to this repository state"
        >
          <KeyValueGrid
            demo={demo}
            columns={2}
            items={[
              { label: "Repository", value: repository?.name },
              { label: "Branch", value: repository?.defaultBranch, mono: true },
              { label: "Commit", value: repository?.commit, mono: true },
              { label: "Commit date", value: fmtDateTime(repository?.commitDate) },
              { label: "Release", value: repository?.release },
              { label: "Scan id", value: scan.scanId, mono: true },
            ]}
          />
          {languages.length > 0 && (
            <div className="mt-4">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
                Language mix
              </div>
              <BarList
                ariaLabel="Language mix by provider-reported byte share"
                items={languages.slice(0, 7).map((language) => ({
                  label: language.name || "Unknown",
                  value: language.percent ?? language.loc ?? 0,
                  display: fmtPercent(language.percent) ?? fmtNumber(language.loc),
                  colorClass: "bg-chart1/80",
                }))}
              />
            </div>
          )}
        </SectionPanel>
      </div>

      {scan.measured && (scan.measured.filesInRepository ?? 0) > 0 && (
        <SectionPanel
          title="Repository composition"
          description="File inventory the scan reasons over, measured from the repository tree"
        >
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8">
            <MetricTile label="Files" value={fmtNumber(scan.measured.filesInRepository)} demo={demo} />
            <MetricTile label="Source" value={fmtNumber(scan.measured.sourceFiles)} demo={demo} />
            <MetricTile label="Tests" value={fmtNumber(scan.measured.testFiles)} demo={demo} />
            <MetricTile label="Manifests" value={fmtNumber(scan.measured.manifestFiles)} demo={demo} />
            <MetricTile label="Lockfiles" value={fmtNumber(scan.measured.lockFiles)} demo={demo} />
            <MetricTile label="IaC" value={fmtNumber(scan.measured.iacFiles)} demo={demo} />
            <MetricTile label="Containers" value={fmtNumber(scan.measured.containerFiles)} demo={demo} />
            <MetricTile
              label="Repo size"
              value={scan.measured.repositorySizeKb !== undefined ? `${fmtNumber(Math.round((scan.measured.repositorySizeKb ?? 0) / 1024))} MB` : undefined}
              demo={demo}
            />
          </div>
        </SectionPanel>
      )}

      <div className="grid gap-4 xl:grid-cols-2">
        <SectionPanel
          title="Exclusions"
          description="What was deliberately not analyzed, and why"
          flush
        >
          {scan.exclusions.length ? (
            <div className="overflow-x-auto">
              <table className="asr-table">
                <thead>
                  <tr>
                    <th scope="col">Pattern</th>
                    <th scope="col" className="text-right">Files</th>
                    <th scope="col">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {scan.exclusions.map((exclusion, index) => (
                    <tr key={exclusion.pattern || index}>
                      <td className="whitespace-nowrap font-mono text-xs">{exclusion.pattern}</td>
                      <td className="whitespace-nowrap text-right tabular-nums">
                        {fmtNumber(exclusion.files) ?? "—"}
                      </td>
                      <td className="max-w-sm text-muted">{exclusion.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-4">
              <EmptyPanel demo={demo} detail="No exclusions were recorded for this scan." />
            </div>
          )}
        </SectionPanel>

        <SectionPanel
          title="Known limitations"
          description="Boundaries the pipeline reported on its own conclusions"
        >
          {scan.limitations.length ? (
            <ul className="space-y-2">
              {scan.limitations.map((limitation, index) => (
                <li key={index} className="flex items-start gap-2 text-xs leading-5 text-text2">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-warning" aria-hidden="true" />
                  {limitation}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyPanel demo={demo} detail="The pipeline reported no limitations — treat with skepticism and validate." />
          )}
        </SectionPanel>
      </div>

      <SectionPanel
        title="Pipeline provenance"
        description="Components that produced this assessment, with run outcomes"
        flush
      >
        {provenance.length ? (
          <div className="overflow-x-auto">
            <table className="asr-table">
              <thead>
                <tr>
                  <th scope="col">Component</th>
                  <th scope="col">Version</th>
                  <th scope="col">Evidence mode</th>
                  <th scope="col">Started</th>
                  <th scope="col">Completed</th>
                  <th scope="col">Result</th>
                </tr>
              </thead>
              <tbody>
                {provenance.map((run, index) => (
                  <tr key={run.component || index}>
                    <td className="font-medium text-text">{run.component}</td>
                    <td className="whitespace-nowrap font-mono text-xs">{run.version || "—"}</td>
                    <td className="whitespace-nowrap">
                      <Pill tone={run.mode === "current evidence" ? "success" : "warning"}>
                        {run.mode || "—"}
                      </Pill>
                    </td>
                    <td className="whitespace-nowrap text-muted">{fmtDateTime(run.started) || "—"}</td>
                    <td className="whitespace-nowrap text-muted">{fmtDateTime(run.completed) || "—"}</td>
                    <td className="whitespace-nowrap">
                      <StatusPill status={run.result} demo={demo} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            <EmptyPanel demo={demo} />
          </div>
        )}
      </SectionPanel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Architecture
// ---------------------------------------------------------------------------

const NODE_TYPE_META: Record<string, { label: string; icon: React.ElementType; tone: string }> = {
  "trust-boundary": { label: "Trust boundary", icon: Globe, tone: "border-critical/40 bg-critical/5" },
  service: { label: "Service", icon: Server, tone: "border-border/70 bg-surface1" },
  datastore: { label: "Data store", icon: Lock, tone: "border-info/30 bg-info/5" },
};

export function ArchitectureView({
  assessment,
  demo,
  onSelectFindingId,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
  onSelectFindingId: (findingId: string) => void;
}) {
  const architecture = selectArchitecture(assessment);
  const composition = selectComposition(assessment);
  const findingById = new Map(
    assessment.findings.map((finding) => [finding.id, finding] as [string, AssuranceFinding])
  );

  const purposeSegments = [
    { label: "Production", value: composition.productionPercent ?? 0, colorClass: "bg-chart1" },
    { label: "Tests", value: composition.testPercent ?? 0, colorClass: "bg-chart2" },
    { label: "Infrastructure", value: composition.infrastructurePercent ?? 0, colorClass: "bg-chart3" },
    { label: "Generated", value: composition.generatedPercent ?? 0, colorClass: "bg-chart4" },
    { label: "Docs", value: composition.documentationPercent ?? 0, colorClass: "bg-chart5" },
  ];
  // Provider-measured file-type composition from the repository tree.
  const fileSegments = [
    { label: "Source", value: composition.sourceFiles ?? 0, colorClass: "bg-chart1" },
    { label: "Tests", value: composition.testFiles ?? 0, colorClass: "bg-chart2" },
    { label: "Config", value: composition.configFiles ?? 0, colorClass: "bg-chart3" },
    { label: "Docs", value: composition.docFiles ?? 0, colorClass: "bg-chart4" },
    { label: "Manifests + locks", value: (composition.manifestFiles ?? 0) + (composition.lockFiles ?? 0), colorClass: "bg-chart5" },
  ];
  const compositionSegments = purposeSegments.some((s) => s.value > 0) ? purposeSegments : fileSegments;
  const hasComposition = compositionSegments.some((segment) => segment.value > 0);
  const compositionDescription = purposeSegments.some((s) => s.value > 0)
    ? "Share of the codebase by purpose"
    : "File-type breakdown from the repository tree (provider-measured)";
  const hasFileTree = (composition.totalFiles ?? 0) > 0;

  return (
    <div className="space-y-4">
      {architecture.style && (
        <CaveatNote tone="brand">
          <span className="font-semibold text-text">Observed style: </span>
          {architecture.style}
        </CaveatNote>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {hasFileTree ? (
          <>
            <MetricTile label="Files in repository" value={fmtNumber(composition.totalFiles)} demo={demo} />
            <MetricTile label="Source files" value={fmtNumber(composition.sourceFiles)} demo={demo} />
            <MetricTile label="Test files" value={fmtNumber(composition.testFiles)} demo={demo} />
            <MetricTile label="Directories" value={fmtNumber(composition.directories)} demo={demo} />
            <MetricTile label="Manifests" value={fmtNumber(composition.manifestFiles)} demo={demo} />
            <MetricTile
              label="Repository size"
              value={composition.repositorySizeKb !== undefined ? `${fmtNumber(Math.round((composition.repositorySizeKb ?? 0) / 1024))} MB` : undefined}
              demo={demo}
            />
          </>
        ) : (
          <>
            <MetricTile label="Services" value={fmtNumber(composition.services)} demo={demo} />
            <MetricTile label="Shared packages" value={fmtNumber(composition.sharedPackages)} demo={demo} />
            <MetricTile label="Deployable images" value={fmtNumber(composition.deployableImages)} demo={demo} />
            <MetricTile label="Databases" value={fmtNumber(composition.databases)} demo={demo} />
            <MetricTile label="Event topics" value={fmtNumber(composition.eventTopics)} demo={demo} />
          </>
        )}
      </div>

      {hasComposition && (
        <SectionPanel
          title="Code composition"
          description={compositionDescription}
        >
          <SegmentStrip segments={compositionSegments} ariaLabel="Code composition" />
          {hasFileTree && composition.largestDirectories.length > 0 && (
            <div className="mt-4">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted">
                Largest top-level directories
              </div>
              <BarList
                ariaLabel="Files by top-level directory"
                items={composition.largestDirectories.slice(0, 6).map((dir) => ({
                  label: dir.path || "—",
                  value: dir.files ?? 0,
                  display: fmtNumber(dir.files),
                  colorClass: "bg-chart2/80",
                }))}
              />
            </div>
          )}
        </SectionPanel>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <SectionPanel
          title="Observed components"
          description="Services and boundaries recovered from the repository"
        >
          {architecture.nodes.length ? (
            <ul className="grid gap-2 sm:grid-cols-2">
              {architecture.nodes.map((node, index) => {
                const meta = NODE_TYPE_META[node.type || ""] || NODE_TYPE_META.service;
                const Icon = meta.icon;
                return (
                  <li
                    key={node.id || index}
                    className={cn("rounded-lg border px-3 py-2.5", meta.tone)}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="h-3.5 w-3.5 flex-none text-muted" aria-hidden="true" />
                      <span className="truncate text-[13px] font-semibold text-text">
                        {node.label || node.id}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted">
                      <span>{meta.label}</span>
                      {node.exposure && (
                        <Pill tone={node.exposure === "internet" ? "critical" : "neutral"}>
                          {node.exposure}
                        </Pill>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyPanel demo={demo} />
          )}
        </SectionPanel>

        <SectionPanel
          title={architecture.edges.length ? "Data flows" : "Evidence by component"}
          description={
            architecture.edges.length
              ? "Connections between components; boundary crossings highlighted"
              : "Where each analysis domain produced findings across the repository structure"
          }
          flush
        >
          {architecture.edges.length ? (
            <div className="overflow-x-auto">
              <table className="asr-table">
                <thead>
                  <tr>
                    <th scope="col">Flow</th>
                    <th scope="col">Protocol</th>
                    <th scope="col">Payload</th>
                    <th scope="col">Boundary</th>
                  </tr>
                </thead>
                <tbody>
                  {architecture.edges.map((edge, index) => (
                    <tr key={index}>
                      <td className="whitespace-nowrap font-mono text-xs">
                        {edge.from} <ArrowRight className="inline h-3 w-3 text-muted" aria-hidden="true" /> {edge.to}
                      </td>
                      <td className="whitespace-nowrap">{edge.protocol || "—"}</td>
                      <td className="max-w-xs text-muted">{edge.data || "—"}</td>
                      <td className="whitespace-nowrap">
                        {edge.trustBoundary ? (
                          <Pill tone="critical">Crosses boundary</Pill>
                        ) : (
                          <span className="text-muted">Internal</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (() => {
            // Real evidence matrix: open findings grouped by top-level
            // component × analysis domain. Measured locations only.
            const byComponent = new Map<string, Map<string, number>>();
            for (const finding of assessment.findings) {
              if (finding.status.toLowerCase() !== "open" || !finding.file) continue;
              const component = finding.file.includes("/") ? finding.file.split("/")[0] : "(repository root)";
              const domains = byComponent.get(component) || new Map<string, number>();
              domains.set(finding.domain, (domains.get(finding.domain) || 0) + 1);
              byComponent.set(component, domains);
            }
            const rows = [...byComponent.entries()]
              .map(([component, domains]) => ({
                component,
                total: [...domains.values()].reduce((sum, count) => sum + count, 0),
                domains,
              }))
              .sort((a, b) => b.total - a.total)
              .slice(0, 8);
            return rows.length ? (
              <div className="overflow-x-auto">
                <table className="asr-table">
                  <thead>
                    <tr>
                      <th scope="col">Component</th>
                      <th scope="col">Evidence domains</th>
                      <th scope="col" className="text-right">Open findings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.component}>
                        <td className="whitespace-nowrap font-mono text-xs font-semibold text-text">{row.component}</td>
                        <td>
                          <div className="flex flex-wrap gap-1.5">
                            {[...row.domains.entries()]
                              .sort((a, b) => b[1] - a[1])
                              .map(([domain, count]) => (
                                <Pill key={domain} tone="neutral">
                                  {domain} · {count}
                                </Pill>
                              ))}
                          </div>
                        </td>
                        <td className="whitespace-nowrap text-right font-semibold tabular-nums">{row.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-4">
                <EmptyPanel demo={demo} />
              </div>
            );
          })()}
        </SectionPanel>
      </div>

      <SectionPanel
        title="Attack surfaces"
        description="Exposed entry points and the priority findings that reach them"
      >
        {architecture.attackSurfaces.length ? (
          <div className="grid gap-3 md:grid-cols-2">
            {architecture.attackSurfaces.map((surface, index) => (
              <div key={surface.surface || index} className="rounded-lg border border-border/70 bg-surface2/40 p-3.5">
                <div className="text-[13px] font-semibold text-text">{surface.surface}</div>
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
                  {surface.routes !== undefined && (
                    <span>
                      <span className="font-semibold tabular-nums text-text">{surface.routes}</span> routes
                    </span>
                  )}
                  {surface.authModes !== undefined && (
                    <span>
                      <span className="font-semibold tabular-nums text-text">{surface.authModes}</span> auth modes
                    </span>
                  )}
                </div>
                {surface.priorityFindings.length > 0 && (
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {surface.priorityFindings.map((findingId) => (
                      <FindingLink
                        key={findingId}
                        id={findingId}
                        finding={findingById.get(findingId)}
                        onOpen={(item) => onSelectFindingId(item.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <EmptyPanel demo={demo} />
        )}
      </SectionPanel>

      <CaveatNote>
        <span className="inline-flex items-center gap-1.5">
          <GitCommitHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
          Architecture is recovered from repository evidence (manifests, IaC, and
          code structure). Runtime topology is <strong>not validated</strong> against a
          live environment{demo ? " in this synthetic assessment" : ""}.
        </span>
      </CaveatNote>
    </div>
  );
}
